import {
	makeBankPaymentPayload,
	makePayment as makePaymentPayload,
	PaymentsHistoryData,
} from 'utils/api/payloads/omantelApi';
import api from 'utils/api';
import { map, catchError } from 'rxjs/internal/operators';
import { throwError, Observable } from 'rxjs';
import invalidateCache from 'utils/api/invalidateCache';
import { Payments } from 'utils/api/models/payments/payments';
import { PaymentDetails } from 'utils/api/models/payments/paymentDetails';
import { IPaymentTokens } from 'utils/api/models/payments/paymentTokens';
import { AjaxResponse } from 'rxjs/ajax';

export class PaymentService {
	public static makePayment(makePaymentProps: types.payment.MakePayment) {
		const payload = makePaymentPayload(makePaymentProps);

		return api.omantelApi
			.makePayment()
			.setPayload(payload)
			.disableCache()
			.call()
			.pipe(
				map((r) => {
					this.invalidateCacheAfterPayment(payload.type);

					let result;
					if (r.response.responseJSON) {
						result = r.response.responseJSON.NullResult;
					} else {
						result = r.response.NullResult;
					}
					return { result };
				}),
				catchError((e: any) => {
					return throwError(e);
				})
			);
	}

	public static payWithBOM(paymentProps: types.payment.BankPayment) {
		const payload = makeBankPaymentPayload(paymentProps);
		return api.omantelMicroApi
			.makeBOMPayment()
			.setPayload(payload)
			.setCustomHeaders()
			.call();
	}

	public static getPaymentTokensV2(): Observable<AjaxResponse> {
		return api.omantelMicroApi
			.getPaymentTokens()
			.disableCache()
			.call();
	}

	public static getPaymentSummary(payload: any): Observable<AjaxResponse> {
		return api.omantelMicroApi
			.getPaymentSummary()
			.setPayload(payload)
			.disableCache()
			.call();
	}

	public static getPaymentsHistoryForAccounts(paymentsHistoryData: PaymentsHistoryData): Observable<Payments> {
		return api.omantelApi
			.getPaymentsHistoryForAccounts(paymentsHistoryData)
			.withErrorHandling()
			.call();
	}

	public static getPaymentHistoryRowItems(paymentRequestId: string): Observable<PaymentDetails> {
		return api.omantelApi
			.getPaymentHistoryRowItems(paymentRequestId)
			.withErrorHandling()
			.call();
	}

	public static getPaymentReceipt(paymentId: string): Observable<any> {
		return api.omantelApi
			.getPaymentReceipt(paymentId)
			.disableCache()
			.withErrorHandling()
			.call();
	}

	public static getPaymentTokens(): Observable<IPaymentTokens> {
		return api.omantelApi.getPaymentTokensForCurrentUser().call();
	}

	public static removePaymentToken(paymentTokenId: string): Observable<any> {
		return api.omantelApi.removePaymentToken(paymentTokenId).call();
	}

	public static getCommitmentDetails(subscriberNo: string): Observable<any> {
		return api.omantelApi.getCommitmentDetails(subscriberNo).call();
	}

	public static invalidateCacheAfterPayment(type: types.payment.paymentType) {
		const cachedMap = new Map<types.payment.paymentType, any>();
		cachedMap.set('TOP_UP', ['getUsage', 'getPaymentTokensForCurrentUser']);
		cachedMap.set('PAY', ['getCustomerAccountBills', 'getPaymentTokensForCurrentUser']);
		cachedMap.set('VOUCHER', ['getPaymentTokensForCurrentUser']);
		if (cachedMap.has(type)) {
			invalidateCache(cachedMap.get(type) as any, true);
		}
	}

	public static getPaymentsRequestStatus(
		pageNumber: number = 0,
		pageSize: number = 10,
		fromDate: string,
		toDate: string,
		paymentStatus: string,
		billNumber: string
	): Observable<any> {
		return api.omantelMicroApi
			.getPaymentsRequestStatus(pageNumber, pageSize, fromDate, toDate, paymentStatus, billNumber)
			.call();
	}

	public static getRefreshPayment(transactionReferenceNumber: string): Observable<any> {
		return api.omantelMicroApi
			.getRefreshPayment()
			.withErrorHandling()
			.setPayload({ transactionReferenceNumber })
			.call();
	}

	//This API is to fetch the bills history
	public static fetchPaymentHistory(data: any): Observable<AjaxResponse> {
		return api.omantelMicroApi
			.fetchPaymentHistory(data.fromDate, data.toDate)
			.withErrorHandling()
			.disableCache()
			.setCustomHeaders()
			.call();
	}

	public static deletePaymentToken(tokenType: string, id: string): Observable<AjaxResponse> {
		return api.omantelMicroApi.deletePaymentToken(tokenType, id).call();
	}
}

export default PaymentService;
